import { ContractorSelector } from '@/components/forms/ContractorSelector';
import { LenderSelector } from '@/components/forms/LenderSelector';
import type {
  CreateListingInput,
  ListingContractor,
  ListingLender,
  ListingStatus,
} from '@/types/listing';

interface ListingStep3Props {
  data: Partial<CreateListingInput>;
  onChange: (updates: Partial<CreateListingInput>) => void;
}

export function ListingStep3({ data, onChange }: ListingStep3Props) {
  return (
    <div className="space-y-[24px]">
      {/* Property Details */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">Property Details</h3>

        <div className="space-y-[16px]">
          <label className="block">
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
              Description
            </span>
            <textarea
              value={data.description || ''}
              onChange={(e) => onChange({ description: e.target.value })}
              rows={4}
              className="w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] py-[8px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              placeholder="Detailed property description..."
            />
          </label>

          <label className="block">
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
              Location Details
            </span>
            <textarea
              value={data.locationDetails || ''}
              onChange={(e) => onChange({ locationDetails: e.target.value })}
              rows={2}
              className="w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] py-[8px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              placeholder="Information about the neighborhood, nearby amenities..."
            />
          </label>

          <label className="block">
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
              Property Details
            </span>
            <textarea
              value={data.propertyDetails || ''}
              onChange={(e) => onChange({ propertyDetails: e.target.value })}
              rows={4}
              className="w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] py-[8px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              placeholder="Detailed property information (beds, baths, features, upgrades, etc.)..."
            />
          </label>

          <div className="grid grid-cols-5 gap-[12px]">
            <label className="block">
              <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">Beds</span>
              <input
                type="number"
                value={data.beds || ''}
                onChange={(e) => onChange({ beds: parseInt(e.target.value, 10) || undefined })}
                className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                placeholder="3"
              />
            </label>
            <label className="block">
              <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">Baths</span>
              <input
                type="number"
                step="0.5"
                value={data.baths || ''}
                onChange={(e) => onChange({ baths: parseFloat(e.target.value) || undefined })}
                className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                placeholder="2"
              />
            </label>
            <label className="block">
              <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
                Sq. Feet
              </span>
              <input
                type="number"
                value={data.squareFeet || ''}
                onChange={(e) =>
                  onChange({ squareFeet: parseInt(e.target.value, 10) || undefined })
                }
                className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                placeholder="1800"
              />
            </label>
            <label className="block">
              <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
                Year Built
              </span>
              <input
                type="number"
                value={data.yearBuilt || ''}
                onChange={(e) => onChange({ yearBuilt: parseInt(e.target.value, 10) || undefined })}
                className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                placeholder="1950"
              />
            </label>
            <label className="block">
              <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
                Lot Size
              </span>
              <input
                type="text"
                value={data.lotSize || ''}
                onChange={(e) => onChange({ lotSize: e.target.value })}
                className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                placeholder="0.25 acres"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Contractors */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">Assigned Contractors</h3>
        <p className="mb-[16px] text-[13px] text-[#8d8c8f]">
          Add contractors with their renovation estimates for this property
        </p>
        <ContractorSelector
          value={(data.contractors as ListingContractor[]) || []}
          onChange={(contractors) => onChange({ contractors })}
          label=""
          maxContractors={10}
        />
      </div>

      {/* Lenders */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">Available Lenders</h3>
        <p className="mb-[16px] text-[13px] text-[#8d8c8f]">
          Select lenders that can provide financing for this property
        </p>
        <LenderSelector
          value={(data.lenders as ListingLender[]) || []}
          onChange={(lenders) => onChange({ lenders })}
          label=""
          maxLenders={10}
        />
      </div>

      {/* Status */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">Listing Status</h3>

        <div className="flex gap-[16px]">
          <label className="flex cursor-pointer items-center gap-[8px]">
            <input
              type="radio"
              name="status"
              value="DRAFT"
              checked={data.status === 'DRAFT' || !data.status}
              onChange={() => onChange({ status: 'DRAFT' as ListingStatus })}
              className="h-[18px] w-[18px] border-[#e5e5e5] text-[#055135] focus:ring-[#055135]"
            />
            <span className="text-[13px] text-[#181818]">Draft</span>
            <span className="text-[11px] text-[#8d8c8f]">(Not visible to public)</span>
          </label>

          <label className="flex cursor-pointer items-center gap-[8px]">
            <input
              type="radio"
              name="status"
              value="PUBLISHED"
              checked={data.status === 'PUBLISHED'}
              onChange={() => onChange({ status: 'PUBLISHED' as ListingStatus })}
              className="h-[18px] w-[18px] border-[#e5e5e5] text-[#055135] focus:ring-[#055135]"
            />
            <span className="text-[13px] text-[#181818]">Published</span>
            <span className="text-[11px] text-[#8d8c8f]">(Visible to public)</span>
          </label>

          <label className="flex cursor-pointer items-center gap-[8px]">
            <input
              type="radio"
              name="status"
              value="ARCHIVED"
              checked={data.status === 'ARCHIVED'}
              onChange={() => onChange({ status: 'ARCHIVED' as ListingStatus })}
              className="h-[18px] w-[18px] border-[#e5e5e5] text-[#055135] focus:ring-[#055135]"
            />
            <span className="text-[13px] text-[#181818]">Archived</span>
            <span className="text-[11px] text-[#8d8c8f]">(Hidden from public)</span>
          </label>
        </div>
      </div>
    </div>
  );
}
